import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    IconButton,
    Paper,
    Typography,
    TextField,
    Avatar,
    Button,
    CircularProgress,
    alpha,
    Slide,
    Fade,
    Link,
    Tooltip,
    useMediaQuery,
} from '@mui/material';
import {
    Send as SendIcon,
    Close as CloseIcon,
    AutoAwesome as BotIcon,
    RocketLaunch as RocketIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { supportChatApi, SupportChatRequest } from '../utils/supportChatApi';
import { agentApi } from '../utils/agentApi';
import { Agent, AgentListQuery } from '../types/agent';
import AgentCreationModeDialog from './admin/AgentCreationModeDialog';
import AgentFormDialog from './admin/AgentFormDialog';
import TemplateSelectionDialog from './admin/TemplateSelectionDialog';
import DuplicateAgentDialog from './admin/DuplicateAgentDialog';
import toast from 'react-hot-toast';

interface Message {
    id: string;
    content: string;
    isUser: boolean;
    timestamp: Date;
}

interface SupportChatWidgetProps {
    isOpen: boolean;
    onToggle: () => void;
}

const SupportChatWidget: React.FC<SupportChatWidgetProps> = ({ isOpen, onToggle }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Estados para controlar a mensagem flutuante de onboarding
    const [hasAgents, setHasAgents] = useState<boolean>(true);
    const [showOnboardingMessage, setShowOnboardingMessage] = useState<boolean>(false);
    const [checkingAgents, setCheckingAgents] = useState<boolean>(true);
    
    const [creationModeOpen, setCreationModeOpen] = useState<boolean>(false);
    const [formDialogOpen, setFormDialogOpen] = useState<boolean>(false);
    const [templateDialogOpen, setTemplateDialogOpen] = useState<boolean>(false);
    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState<boolean>(false);
    const [editingAgentId, setEditingAgentId] = useState<string | undefined>(undefined);
    const [initialAgentData, setInitialAgentData] = useState<any | undefined>(undefined);

    const ONBOARDING_STORAGE_KEY = 'supportChat_onboarding_dismissed';
    const ONBOARDING_REAPPEAR_MINUTES = 30;

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleManageWithAI = () => {
        setCreationModeOpen(false);
        navigate('/admin/');
    };

    const handleAgentCreated = (newAgent: Agent) => {  
        console.log('Agente criado:', newAgent);
        toast.success('Agente criado com sucesso!');
        setFormDialogOpen(false);

        setShowOnboardingMessage(false);
        navigate('/admin/agents');
        setTimeout(() => {
            window.location.reload();
        }, 100);
    };

    // Verifica se deve mostrar a mensagem de onboarding
    const shouldShowOnboardingMessage = (): boolean => {
        const dismissedData = localStorage.getItem(ONBOARDING_STORAGE_KEY);

        if (!dismissedData) {
            return true; // Nunca foi fechada, mostrar
        }

        try {
            const dismissedTimestamp = parseInt(dismissedData, 10);
            const now = Date.now();
            const timeDiffMinutes = (now - dismissedTimestamp) / (1000 * 60);

            // Se passaram mais de 30 minutos, mostrar novamente
            return timeDiffMinutes >= ONBOARDING_REAPPEAR_MINUTES;
        } catch (error) {
            return true; // Se houver erro ao ler, mostrar
        }
    };

    // Fecha a mensagem de onboarding e salva no localStorage
    const dismissOnboardingMessage = () => {
        setShowOnboardingMessage(false);
        localStorage.setItem(ONBOARDING_STORAGE_KEY, Date.now().toString());
    };

    // Verifica se o usuário tem agentes criados
    useEffect(() => {
        const checkUserAgents = async () => {
            try {
                setCheckingAgents(true);
                const query: AgentListQuery = {
                    pageIndex: 1,
                    pageSize: 1,
                    agentName: '',
                    agentStatus: ''
                };

                const agents = await agentApi.getAgentsList(query);
                const userHasAgents = agents && agents.length > 0;

                setHasAgents(userHasAgents);

                // Só mostrar a mensagem se:
                // 1. Não tem agentes
                // 2. O chat está fechado
                // 3. Deve mostrar conforme o timer de 30 minutos
                if (!userHasAgents && !isOpen && shouldShowOnboardingMessage()) {
                    setShowOnboardingMessage(true);
                }
            } catch (error) {
                console.error('Erro ao verificar agentes:', error);
                setHasAgents(true); // Em caso de erro, assumir que tem agentes para não mostrar mensagem
            } finally {
                setCheckingAgents(false);
            }
        };

        checkUserAgents();

        // Revalidar a cada 5 minutos
        const interval = setInterval(checkUserAgents, 5 * 60 * 1000);

        return () => clearInterval(interval);
    }, [isOpen]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSendMessage = async () => {
        if (!inputValue.trim() || isLoading) return;

        const userMessage: Message = {
            id: Date.now().toString(),
            content: inputValue.trim(),
            isUser: true,
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setIsLoading(true);

        try {
            const request: SupportChatRequest = {
                message: userMessage.content,
                conversationHistory: messages.slice(-5).map(msg => ({
                    content: msg.content,
                    isUser: msg.isUser,
                    timestamp: msg.timestamp
                })), // Últimas 5 mensagens para contexto
            };

            const data = await supportChatApi.sendMessage(request);

            const botMessage: Message = {
                id: (Date.now() + 1).toString(),
                content: data.response,
                isUser: false,
                timestamp: new Date(),
            };

            setMessages(prev => [...prev, botMessage]);
        } catch (error) {
            console.error('Erro ao enviar mensagem:', error);
            const errorMessage: Message = {
                id: (Date.now() + 1).toString(),
                content: 'Desculpe, ocorreu um erro ao processar sua mensagem. Tente novamente.',
                isUser: false,
                timestamp: new Date(),
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSendMessage();
        }
    };

    const formatTime = (date: Date) => {
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    const renderMessageContent = (content: string) => {
        // Regex para encontrar links no formato [texto](/path)
        const linkRegex = /\[([^\]]+)\]\(\/([^)]+)\)/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = linkRegex.exec(content)) !== null) {
            // Adicionar texto antes do link
            if (match.index > lastIndex) {
                parts.push(content.slice(lastIndex, match.index));
            }

            // Adicionar o link
            const linkText = match[1];
            const linkPath = match[2];
            parts.push(
                <Link
                    key={match.index}
                    component="button"
                    variant="body2"
                    onClick={() => {
                        navigate(`/${linkPath}`);
                        onToggle(); // Fechar o chat após navegar
                    }}
                    sx={{
                        color: theme.palette.primary.main,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        '&:hover': {
                            color: theme.palette.primary.dark,
                        },
                    }}
                >
                    {linkText}
                </Link>
            );

            lastIndex = match.index + match[0].length;
        }

        // Adicionar texto restante
        if (lastIndex < content.length) {
            parts.push(content.slice(lastIndex));
        }

        return parts.length > 0 ? parts : content;
    };

    return (
        <>
            {/* Mensagem flutuante de onboarding - aparece quando não há agentes */}
            <Fade in={showOnboardingMessage && !isOpen} timeout={500}>
                <Paper
                    sx={{
                        position: 'fixed',
                        bottom: { xs: 100, sm: 100 },
                        right: { xs: 16, sm: 24 },
                        left: { xs: 16, sm: 'auto' },
                        width: { xs: 'calc(100vw - 32px)', sm: 320 },
                        maxWidth: { xs: 320, sm: 'none' },
                        zIndex: 1299,
                        p: 2.5,
                        boxShadow: theme.shadows[12],
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        color: 'white',
                        border: `2px solid ${alpha('#fff', 0.2)}`,
                    }}
                >
                    <IconButton
                        onClick={dismissOnboardingMessage}
                        sx={{
                            position: 'absolute',
                            top: 4,
                            right: 6,
                            color: theme.palette.mode === 'dark' ? '#000' : 'white',
                            padding: 0.5,
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.mode === 'dark' ? '#000' : '#fff', 0.15),
                            },
                        }}
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mt: 1 }}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: theme.palette.mode === 'dark' ? alpha('#fff', 0.9) : alpha('#fff', 0.2),
                                flexShrink: 0,
                            }}
                        >
                            <RocketIcon sx={{ fontSize: 24, color: theme.palette.mode === 'dark' ? '#000' : 'white' }} />
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 700,
                                    mb: 1,
                                    fontSize: '1rem',
                                    lineHeight: 1.3,
                                    color: theme.palette.mode === 'dark' ? '#000' : 'white',
                                }}
                            >
                                Pronto para começar?
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '0.875rem',
                                    lineHeight: 1.5,
                                    opacity: 0.95,
                                    mb: 1.5,
                                    color: theme.palette.mode === 'dark' ? '#000' : 'white',
                                }}
                            >
                                Crie seu primeiro agente para começar a usar o sistema! Se precisar de ajuda, estou aqui.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        navigate('/admin/agents');
                                        setCreationModeOpen(true);
                                        dismissOnboardingMessage();
                                    }}
                                    sx={{
                                        backgroundColor: 'white',
                                        color: '#000',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        px: 2,
                                        py: 0.5,
                                        '&:hover': {
                                            backgroundColor: alpha('#fff', 0.9),
                                        },
                                    }}
                                >
                                    Criar Agente
                                </Button>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        onToggle();
                                        dismissOnboardingMessage();
                                    }}
                                    sx={{
                                        color: theme.palette.mode === 'dark' ? '#000' : 'white',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        border: theme.palette.mode === 'dark' ? '1px solid #000' : `1px solid ${alpha('#fff', 0.5)}`,
                                        px: 2,
                                        py: 0.5,
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.mode === 'dark' ? '#000' : '#fff', 0.1),
                                        },
                                    }}
                                >
                                    Preciso de Ajuda
                                </Button>
                            </Box>
                        </Box>
                    </Box>

                    {/* Seta apontando para o botão */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: -8,
                            right: { xs: 16, sm: 24 },
                            width: 16,
                            height: 16,
                            backgroundColor: theme.palette.primary.dark,
                            transform: 'rotate(45deg)',
                            border: `2px solid ${alpha('#fff', 0.2)}`,
                            borderTop: 'none',
                            borderLeft: 'none',
                        }}
                    />
                </Paper>
            </Fade>

            {/* Botão flutuante */}
            <Fade in={!isOpen} timeout={300}>
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: { xs: 16, sm: 24 },
                        right: { xs: 16, sm: 24 },
                        zIndex: 1300,
                    }}
                >
                    <IconButton
                        onClick={onToggle}
                        sx={{
                            width: { xs: 56, sm: 64 },
                            height: { xs: 56, sm: 64 },
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            boxShadow: theme.shadows[8],
                            '&:hover': {
                                backgroundColor: theme.palette.primary.dark,
                                transform: 'scale(1.05)',
                            },
                            transition: 'all 0.3s ease',
                            padding: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <img
                            src="/lia-profile.jpeg"
                            alt="Assistente IA"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    </IconButton>
                </Box>
            </Fade>

            {/* Janela de chat */}
            <Slide direction="up" in={isOpen} timeout={300}>
                <Paper
                    sx={{
                        position: 'fixed',
                        bottom: { xs: 16, sm: 24 },
                        right: { xs: 16, sm: 24 },
                        left: { xs: 16, sm: 'auto' },
                        width: { xs: 'calc(100vw - 32px)', sm: 420 },
                        maxWidth: { xs: 400, sm: 'none' },
                        height: { xs: '70vh', sm: 600 },
                        maxHeight: { xs: '70vh', sm: 'calc(100vh - 48px)' },
                        minHeight: { xs: 450, sm: 'auto' },
                        zIndex: 1300,
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: theme.shadows[12],
                        borderRadius: 3,
                        overflow: 'hidden',
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            p: { xs: 1.5, sm: 2 },
                            background: 'linear-gradient(135deg, #00f5ff 0%, #35e87c 100%)',
                            color: '#1a1a1a',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexShrink: 0,
                            minHeight: { xs: 56, sm: 'auto' },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                                sx={{
                                    width: { xs: 28, sm: 32 },
                                    height: { xs: 28, sm: 32 },
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    border: `2px solid ${alpha('#1a1a1a', 0.3)}`,
                                    flexShrink: 0,
                                }}
                            >
                                <img
                                    src="/lia-profile.jpeg"
                                    alt="Assistente IA"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                            </Box>
                            <Box>
                                <Typography 
                                    variant="subtitle1" 
                                    sx={{ 
                                        fontWeight: 600, 
                                        color: '#1a1a1a',
                                        fontSize: { xs: '0.875rem', sm: '1rem' }
                                    }}
                                >
                                    Lia - Assistente IA
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton
                            onClick={onToggle}
                            sx={{
                                color: '#1a1a1a',
                                '&:hover': {
                                    backgroundColor: alpha('#1a1a1a', 0.1),
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Mensagens */}
                    <Box
                        sx={{
                            flex: 1,
                            overflow: 'auto',
                            p: 1,
                            backgroundColor: theme.palette.background.default,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                        }}
                    >
                        {messages.length === 0 && (
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    py: { xs: 3, sm: 6 },
                                    px: { xs: 2, sm: 3 },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        mb: { xs: 2, sm: 3 },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: { xs: 60, sm: 80 },
                                            height: { xs: 60, sm: 80 },
                                            borderRadius: '50%',
                                            overflow: 'hidden',
                                            boxShadow: theme.shadows[4],
                                            border: `3px solid ${theme.palette.primary.main}`,
                                        }}
                                    >
                                        <img
                                            src="/lia-profile.jpeg"
                                            alt="Assistente IA"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mb: { xs: 1.5, sm: 2 },
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                        fontSize: { xs: '1rem', sm: '1.25rem' },
                                    }}
                                >
                                    Olá! 👋
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.5,
                                        mb: { xs: 2, sm: 3 },
                                        fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                    }}
                                >
                                    Sou seu assistente de suporte. <br /> Posso ajudá-lo com dúvidas sobre:
                                </Typography>
                                <Box sx={{ textAlign: 'center', maxWidth: 320, mx: 'auto' }}>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, display: 'block', mb: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                                        • Criação e configuração de agentes
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, display: 'block', mb: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                                        • Gestão de leads e usuários
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, display: 'block', mb: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                                        • Relatórios e configurações
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, display: 'block', fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                                        • Integração com WhatsApp
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {messages.map((message) => (
                            <Box
                                key={message.id}
                                sx={{
                                    display: 'flex',
                                    justifyContent: message.isUser ? 'flex-end' : 'flex-start',
                                    alignItems: 'flex-start',
                                    gap: 1.5,
                                    mb: 1,
                                }}
                            >
                                {!message.isUser && (
                                    <Box
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            overflow: 'hidden',
                                            mt: 0.5,
                                            boxShadow: theme.shadows[2],
                                            border: `2px solid ${theme.palette.primary.main}`,
                                        }}
                                    >
                                        <img
                                            src="/lia-profile.jpeg"
                                            alt="Assistente IA"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </Box>
                                )}
                                <Box
                                    sx={{
                                        maxWidth: '75%',
                                        backgroundColor: message.isUser
                                            ? (theme.palette.mode === 'dark' ? '#fff' : theme.palette.primary.main)
                                            : theme.palette.background.paper,
                                        color: message.isUser
                                            ? (theme.palette.mode === 'dark' ? '#000' : 'white')
                                            : theme.palette.text.primary,
                                        p: 2,
                                        borderRadius: message.isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                        boxShadow: message.isUser
                                            ? theme.shadows[3]
                                            : theme.shadows[1],
                                        position: 'relative',
                                        border: `1px solid ${message.isUser
                                            ? (theme.palette.mode === 'dark' ? '#fff' : theme.palette.primary.dark)
                                            : theme.palette.divider}`,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            whiteSpace: 'pre-wrap',
                                            lineHeight: 1.4,
                                            fontSize: '0.9rem',
                                            '& a': {
                                                color: message.isUser ? theme.palette.mode === 'dark' ? '#000' : undefined : theme.palette.primary.main,
                                                textDecoration: 'underline',
                                                fontWeight: 500,
                                            }
                                        }}
                                    >
                                        {renderMessageContent(message.content)}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            mt: 1,
                                            opacity: message.isUser ? 0.8 : 0.6,
                                            fontSize: '0.75rem',
                                            fontWeight: 500,
                                        }}
                                    >
                                        {formatTime(message.timestamp)}
                                    </Typography>
                                </Box>
                                {message.isUser && (
                                    <Avatar
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            backgroundColor: theme.palette.secondary.main,
                                            mt: 0.5,
                                            boxShadow: theme.shadows[2],
                                            fontWeight: 'bold',
                                            fontSize: '0.9rem',
                                        }}
                                    >
                                        {message.content.charAt(0).toUpperCase()}
                                    </Avatar>
                                )}
                            </Box>
                        ))}

                        {isLoading && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1.5,
                                    justifyContent: 'flex-start',
                                    mb: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        overflow: 'hidden',
                                        mt: 0.5,
                                        boxShadow: theme.shadows[2],
                                        border: `2px solid ${theme.palette.primary.main}`,
                                    }}
                                >
                                    <img
                                        src="/lia-profile.jpeg"
                                        alt="Assistente IA"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        backgroundColor: theme.palette.background.paper,
                                        p: 2,
                                        borderRadius: '18px 18px 18px 4px',
                                        boxShadow: theme.shadows[1],
                                        border: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={16} sx={{ color: theme.palette.primary.main }} />
                                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                            Digitando...
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                        <div ref={messagesEndRef} />
                    </Box>

                    {/* Input */}
                    <Box
                        sx={{
                            p: { xs: 1.5, sm: 2.5 },
                            borderTop: 1,
                            borderColor: 'divider',
                            backgroundColor: theme.palette.background.paper,
                            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
                            flexShrink: 0,
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
                            <TextField
                                fullWidth
                                multiline
                                maxRows={4}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Digite sua dúvida sobre o sistema..."
                                disabled={isLoading}
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        backgroundColor: theme.palette.background.default,
                                        '& fieldset': {
                                            borderColor: theme.palette.divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme.palette.primary.main,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme.palette.primary.main,
                                            borderWidth: 2,
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: '0.9rem',
                                        lineHeight: 1.4,
                                    }
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleSendMessage}
                                disabled={!inputValue.trim() || isLoading}
                                sx={{
                                    minWidth: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    boxShadow: theme.shadows[3],
                                    color: theme.palette.mode === 'dark' ? '#000' : undefined,
                                    '&:hover': {
                                        transform: 'scale(1.05)',
                                        boxShadow: theme.shadows[6],
                                    },
                                    '&:disabled': {
                                        opacity: 0.5,
                                        transform: 'none',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <SendIcon sx={{ fontSize: 20 }} />
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Slide>

            {/* Modal de criação de agente */}
            <AgentCreationModeDialog
                open={creationModeOpen}
                onClose={() => setCreationModeOpen(false)}
                onCreateFromScratch={() => {
                    setCreationModeOpen(false);
                    setInitialAgentData(undefined);
                    setFormDialogOpen(true);
                }}
                onChooseTemplate={() => {
                    setCreationModeOpen(false);
                    setTemplateDialogOpen(true);
                }}
                onDuplicateFromAgent={() => {
                    setCreationModeOpen(false);
                    setDuplicateDialogOpen(true);
                }}
                onManageWithAI={handleManageWithAI}
            />

            <TemplateSelectionDialog
                open={templateDialogOpen}
                onClose={() => setTemplateDialogOpen(false)}
                onSelectTemplate={(tpl) => {
                    setTemplateDialogOpen(false);
                    setInitialAgentData(tpl);
                    setEditingAgentId(undefined);
                    setFormDialogOpen(true);
                }}
            />

            <DuplicateAgentDialog
                open={duplicateDialogOpen}
                onClose={() => setDuplicateDialogOpen(false)}
                agents={[]}
                onSelectAgentToDuplicate={async (agentId) => {
                    try {
                        setDuplicateDialogOpen(false);
                        const base = await agentApi.getAgent(agentId);
                        const { id, createdAt, updatedAt, ...rest } = base as any;
                        setInitialAgentData({ ...rest, name: `${base.name} (Cópia)` });
                        setEditingAgentId(undefined);
                        setFormDialogOpen(true);
                    } catch (error) {
                        console.error('Error preparing duplicate data:', error);
                    }
                }}
            />

            <AgentFormDialog
                open={formDialogOpen}
                agentId={editingAgentId}
                initialData={initialAgentData}
                onClose={() => setFormDialogOpen(false)}
                onSaved={(agent) => {
                    handleAgentCreated(agent);
                }}
            />
        </>
    );
};

export default SupportChatWidget;
